// This file contains shared graphql queries. It's a good idea to reuse queries that have already been executed even if
// you don't need all the same fields as apollo will fetch the data from cache as opposed to the api. See more here:
// https://www.apollographql.com/docs/react/caching/cache-configuration/
import gql from "graphql-tag"

const CASE_DETAIL_FRAGMENT = gql`
  fragment caseDetailFragment on ContactTracingCase {
    id
    internalId
    type
    archivedAt
    subjects {
      startAt
      endAt
      entity {
        ... on User {
          id
          name
          employeeId
          healthcareRole
          department {
            id
            name
          }
          compliance {
            timeslices {
              full {
                aggregates {
                  overall {
                    compliancePercentage
                    totalOpportunitiesCount
                  }
                }
              }
            }
          }
        }
        ... on Location {
          id
          name
          contactTracingVisits {
            totalCount
          }
        }
      }
    }
  }
`

const CASE_DETAIL_QUERY = gql`
  query ContactTracingCaseDetail($facilityId: ID!, $caseId: ID!) {
    facility(id: $facilityId) {
      id
      contactTracingCases(ids: [$caseId]) {
        ...caseDetailFragment
      }
    }
  }
  ${CASE_DETAIL_FRAGMENT}
`

const CASE_LIST = gql`
  query ContactTracingCaseList($facilityId: ID!, $archived: Boolean!) {
    facility(id: $facilityId) {
      id
      name
      contactTracingCases(archived: $archived) {
        id
        internalId
        type
        createdAt
        updatedAt
        subjects {
          id
          startAt
          endAt
          entity {
            ... on Location {
              id
              name
            }
            ... on User {
              id
              name
            }
          }
        }
      }
    }
  }
`

const EXPOSED_ASSET_LIST = gql`
  query ContactTracingExposedAssetList($facilityId: ID!, $caseId: ID!) {
    facility(id: $facilityId) {
      id
      timezone
      contactTracingCases(ids: [$caseId]) {
        id
        type
        subjects {
          id
          entity {
            ... on User {
              id
              contactTracingVisits {
                edges {
                  node {
                    id
                    location {
                      id
                    }
                    entryAt
                    exitAt
                  }
                }
              }
            }
            ... on Location {
              id
            }
          }
          startAt
          endAt
        }
        exposedAssets {
          id
          firstContact
          lastContact
          exposureDuration
          exposed {
            ... on Asset {
              id
              assetId
              serialNumber
              name
              labels
              manufacturer
              modelName
              ownership
              lastMovedAt
              lastSeenAt
              imageId
              owners {
                id
                name
              }
              location {
                id
                name
                patientArea
                locationType
                unit {
                  id
                  name
                }
              }
            }
          }
        }
      }
    }
  }
`

const EXPOSED_EMPLOYEE_LIST = gql`
  query ContactTracingExposedEmployeeList(
    $facilityId: ID!
    $caseId: ID!
    $startAt: String!
    $endAt: String!
  ) {
    facility(id: $facilityId) {
      id
      timezone
      contactTracingCases(ids: [$caseId]) {
        id
        subjects {
          id
          entity {
            ... on User {
              id
              contactTracingVisits {
                edges {
                  node {
                    id
                    location {
                      id
                    }
                    entryAt
                    exitAt
                  }
                }
              }
            }
            ... on Location {
              id
            }
          }
          startAt
          endAt
        }
        exposedUsers {
          id
          exposed {
            ... on User {
              id
              name
              employeeId
              healthcareRole
              department {
                id
                name
              }
              compliance(startAt: $startAt, endAt: $endAt) {
                timeslices {
                  full {
                    aggregates {
                      overall {
                        compliancePercentage
                      }
                    }
                  }
                }
              }
            }
          }
          firstContact
          lastContact
          exposureDuration
        }
      }
    }
  }
`

const EXPOSED_LOCATION_LIST = gql`
  query ContactTracingExposedLocationList(
    $facilityId: ID!
    $caseId: ID!
    $startAt: String!
    $endAt: String!
  ) {
    facility(id: $facilityId) {
      id
      timezone
      contactTracingCases(ids: [$caseId]) {
        id
        type
        subjects {
          id
          entity {
            ... on User {
              id
              contactTracingVisits {
                edges {
                  node {
                    id
                    location {
                      id
                    }
                    entryAt
                    exitAt
                  }
                }
              }
            }
            ... on Location {
              id
            }
          }
          startAt
          endAt
        }
        exposedLocations {
          id
          exposed {
            ... on Location {
              id
              name
              unit {
                id
                name
              }
              department {
                id
                name
              }
              compliance(startAt: $startAt, endAt: $endAt) {
                timeslices {
                  full {
                    aggregates {
                      overall {
                        compliancePercentage
                      }
                    }
                  }
                }
              }
            }
          }
          firstContact
          lastContact
          exposureDuration
        }
      }
    }
  }
`

const LOCATIONS_QUERY = gql`
  query ContactTracingFacilityLocations($facilityId: ID!) {
    facility(id: $facilityId) {
      id
      timezone
      listItems: locations(contactTracing: true) {
        id
        name
        subName: unit {
          id
          name
        }
      }
    }
  }
`

const USERS_QUERY = gql`
  query ContactTracingFacilityUsers($facilityId: ID!) {
    facility(id: $facilityId) {
      id
      timezone
      listItems: users {
        id
        name
        subName: department {
          id
          name
        }
      }
    }
  }
`

export {
  CASE_DETAIL_FRAGMENT,
  CASE_DETAIL_QUERY,
  CASE_LIST,
  EXPOSED_ASSET_LIST,
  EXPOSED_EMPLOYEE_LIST,
  EXPOSED_LOCATION_LIST,
  LOCATIONS_QUERY,
  USERS_QUERY,
}
